import { pctValue } from '../lib/api'

// Band is the primary datum in a table that runs to hundreds of rows, so it is
// rendered as text first, then a distinct shape, and only then a colour. A
// colour-blind reader, a low-vision reader at 200% zoom and anyone reading a
// greyscale print of this screen all get the band from the same fixed column.
//
// The words are in sentence case. Capitals read as shouting and the redesign
// spec drops uppercase labels everywhere; the shape and colour still carry the
// weight, so "Critical" loses nothing that "CRITICAL" had.

type BandKey = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL' | 'INSUFFICIENT_EVIDENCE'

const SPEC: Record<BandKey, { label: string; cls: string; shape: 'circle' | 'diamond' | 'triangle' | 'hexagon' | 'dashed'; colour: string }> = {
  LOW: { label: 'Low', cls: 'band-low', shape: 'circle', colour: 'var(--band-low)' },
  MEDIUM: { label: 'Medium', cls: 'band-med', shape: 'diamond', colour: 'var(--band-med)' },
  HIGH: { label: 'High', cls: 'band-high', shape: 'triangle', colour: 'var(--band-high)' },
  CRITICAL: { label: 'Critical', cls: 'band-crit', shape: 'hexagon', colour: 'var(--band-crit)' },
  INSUFFICIENT_EVIDENCE: { label: 'Insufficient evidence', cls: 'band-insuf', shape: 'dashed', colour: 'var(--band-insuf)' },
}

function normalise(band: string | undefined | null): BandKey | null {
  if (!band) return null
  const k = band.trim().toUpperCase().replace(/[\s-]+/g, '_')
  return k in SPEC ? (k as BandKey) : null
}

// The band as a word, for a heading or a sentence. "Not scored" when there is
// no band, because an empty cell reads as a gap in the record.
export function bandWord(band: string | undefined | null): string {
  const key = normalise(band)
  return key ? SPEC[key].label : 'Not scored'
}

// The band's colour token, for a shape that is not a chip: the big band word on
// the vendor overview, a sparkline, a driver's swatch.
export function bandColour(band: string | undefined | null): string {
  const key = normalise(band)
  return key ? SPEC[key].colour : 'var(--band-insuf)'
}

// Where a band sits on the scale, low to critical, for a trend line. Insufficient
// evidence is not a point on the scale, so it has no rank.
export function bandRank(band: string | undefined | null): number | null {
  switch (normalise(band)) {
    case 'LOW':
      return 1
    case 'MEDIUM':
      return 2
    case 'HIGH':
      return 3
    case 'CRITICAL':
      return 4
    default:
      return null
  }
}

function Shape({ kind }: { kind: (typeof SPEC)[BandKey]['shape'] }) {
  const common = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.6 }
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false">
      {kind === 'circle' && <circle cx="6" cy="6" r="4.4" {...common} fill="currentColor" />}
      {kind === 'diamond' && <path d="M6 1.2 10.8 6 6 10.8 1.2 6Z" {...common} fill="currentColor" />}
      {kind === 'triangle' && <path d="M6 1.1 11.2 10.6H0.8Z" {...common} fill="currentColor" strokeLinejoin="round" />}
      {kind === 'hexagon' && (
        <path d="M3.6 1.2h4.8L11 6l-2.6 4.8H3.6L1 6Z" {...common} fill="currentColor" strokeLinejoin="round" />
      )}
      {/* The dashed outline says "nothing solid here yet" without a second colour. */}
      {kind === 'dashed' && <rect x="1.4" y="1.4" width="9.2" height="9.2" {...common} strokeDasharray="2.4 1.8" />}
    </svg>
  )
}

export function BandChip({ band, title }: { band: string | undefined | null; title?: string }) {
  const key = normalise(band)
  if (!key) {
    return <span className="band band-insuf band-words">Not scored</span>
  }
  const spec = SPEC[key]
  return (
    <span className={'band band-words ' + spec.cls} title={title}>
      {spec.label}
      <Shape kind={spec.shape} />
    </span>
  )
}

// The band and the attestation ratio travel together. A Critical derived from
// 30% attested answers and one derived from 95% are different claims, and
// separating them on screen is how the weaker one gets read as the stronger.
export function BandWithAttestation({
  band,
  attestationPct,
  thinBelow = 60,
}: {
  band: string | undefined | null
  attestationPct: number | undefined | null
  thinBelow?: number
}) {
  const v = pctValue(attestationPct)
  const thin = v !== null && v < thinBelow
  return (
    <span className="band-pair">
      <BandChip band={band} />
      {v === null ? (
        <span className="attest">attestation not recorded</span>
      ) : (
        <span className={thin ? 'attest thin' : 'attest'} title={thin ? 'Below the attestation level this band assumes' : undefined}>
          {Math.round(v)}% attested
        </span>
      )}
    </span>
  )
}
